import { type IntroCourseLessonCMS } from "@repo/common/src/types/firestore/cms/introCourse";
import { Timestamp } from "firebase/firestore";
import { type FirebaseStorage } from "firebase/storage";

import { assertLessonDurationWithinCap } from "./assertLessonDurationWithinCap";
import { getAudioDuration } from "./getAudioDuration";

interface AudioFileOperationsProps<T extends IntroCourseLessonCMS> {
  file: T;
  originalFile: T | undefined;
  storage: FirebaseStorage;
}

/**
 * Move audio file to the correct storage path, calculate its duration, and add/update timestamps
 */
export async function fileOperationsIntroCourse<T extends IntroCourseLessonCMS>({
  file,
  originalFile,
  storage,
}: AudioFileOperationsProps<T>): Promise<T & { duration: number }> {
  const updatedFile = { ...file };

  // Calculate audio duration
  const duration = await getAudioDuration(updatedFile.file, storage);
  assertLessonDurationWithinCap(duration);

  // Add or update timestamps
  const now = Timestamp.now().toMillis();

  if (!updatedFile.created_on_milis) {
    updatedFile.created_on_milis = now;
  }

  if (shouldUpdateTimestamp(originalFile, updatedFile)) {
    updatedFile.updated_on_milis = now;
  }

  return { ...updatedFile, duration };
}

function hasFileChanged<T extends IntroCourseLessonCMS>(originalFile: T | undefined, updatedFile: T): boolean {
  if (!originalFile) return true;

  const audioFileChanged = originalFile.file !== updatedFile.file;

  return audioFileChanged;
}

function shouldUpdateTimestamp<T extends IntroCourseLessonCMS>(originalFile: T | undefined, updatedFile: T): boolean {
  return hasFileChanged(originalFile, updatedFile) || !updatedFile.updated_on_milis;
}
